import moment from "moment-timezone";
import {useEffect, useState} from "react";
import {Link, useNavigate} from "react-router";

import {useCheckoutContext, useCurrentUserContext} from "@/components/context/hooks.ts";
import {AddedValue, BillingPeriod, Plan} from "@/lib/types/billing.ts";
import {isFreeTrialExpired} from "@/lib/userUtils.ts";
import {AdvancedFeaturesWarning} from "@/pages/billing/subscription/components/AdvancedFeaturesWarning.tsx";
import {CtaBlurbComparisonButtonGroup} from "@/pages/billing/subscription/components/CtaBlurbComparisonButtonGroup.tsx";
import {PlanCustom} from "@/pages/billing/subscription/plan/PlanCustom.tsx";
import {PlanRegular} from "@/pages/billing/subscription/plan/PlanRegular.tsx";

export const Subscription = () => {
  const {user} = useCurrentUserContext();
  const checkoutContext = useCheckoutContext();
  const navigateFn = useNavigate();
  const hasNoSubscription = !checkoutContext.hasActiveSubscription;
  const freeTrialExpired = isFreeTrialExpired(user);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(
    hasNoSubscription ? BillingPeriod.YEAR : user?.account.billing_period || BillingPeriod.YEAR
  );
  const hasActivePremiumFeatures = checkoutContext.hasActivePremiumFeatures;
  const currentPlan: Plan | null = user?.account.plan as Plan | null;
  const tooltip =
    hasNoSubscription || billingPeriod === BillingPeriod.YEAR
      ? undefined
      : 'To change your billing period please go to the "Payment and Invoices" tab.';
  const handleCheckout = (plan: Plan, addedValues: AddedValue[]) => {
    if (
      plan != Plan.Custom &&
      (!currentPlan ||
        (currentPlan === Plan.Starter && (plan === Plan.Essential || plan === Plan.Premium)) ||
        (currentPlan === Plan.Essential && plan === Plan.Premium))
    ) {
      navigateFn("/billing/checkout", {
        state: {
          plan,
          billingPeriod,
          addedValues,
        },
      });
    } else {
      navigateFn("/billing/custom-quote");
    }
  };
  useEffect(() => {
    setBillingPeriod(hasNoSubscription ? BillingPeriod.YEAR : user?.account.billing_period || BillingPeriod.YEAR);
  }, [hasNoSubscription, user]);
  return (
    <>
      <CtaBlurbComparisonButtonGroup
        billingPeriod={billingPeriod}
        onChange={setBillingPeriod}
        text_success={freeTrialExpired ? "" : hasNoSubscription ? "Subscribe Now" : "Upgrade Today"}
        title_cont={
          freeTrialExpired
            ? ""
            : hasNoSubscription
              ? "to the Best Availability Monitoring Platform!"
              : "and Unlock Enhanced Features from the Best Availability Monitoring Platform!"
        }
        copy={
          freeTrialExpired ? (
            <span>
              Your free trial <strong>ended</strong> on{" "}
              {moment(user?.account?.free_trial_expires_at).format("MMM DD, Y")}.
              <strong> All monitoring activities are currently paused. </strong>
              <Link to="/billing/subscribe">Subscribe today </Link>
              to resume comprehensive monitoring and alerts without interruption.
            </span>
          ) : hasNoSubscription ? (
            <span>
              Choose from one of our pre-configured plans to kickstart your monitoring experience effortlessly! Each
              plan is designed for scalability, enabling you to purchase additional add-ons after your initial purchase.
              For a customized plan with a distinct configuration, please reach out to us using the{" "}
              <Link to="/contact">Contact Us</Link> option. We look forward to addressing your monitoring needs no
              matter the size or scope.
            </span>
          ) : (
            <span>
              Upgrade your subscription to one of the plans available below. Each plan is designed for scalability,
              enabling you to purchase additional add-ons after your initial purchase. For a customized plan with a
              distinct configuration, please reach out to us using the <Link to="/contact">Contact Us</Link> option. We
              look forward to addressing your monitoring needs no matter the size or scope.
            </span>
          )
        }
        tooltip={tooltip}
      />
      {hasNoSubscription && hasActivePremiumFeatures && (
        <div className="p-4">
          <AdvancedFeaturesWarning />
        </div>
      )}
      <div className="row align-items-stretch mt-4">
        <div className="col-3">
          <PlanRegular
            plan={Plan.Starter}
            currentPlan={currentPlan as Plan}
            name="Starter"
            subtitle="Get started with monitoring necessities"
            handleCheckout={handleCheckout}
            startHereText="Start here and grow:"
            btnText={
              (currentPlan &&
                {
                  [Plan.Starter]: "Current Plan",
                  [Plan.Essential]: "Contact Us",
                  [Plan.Premium]: "Contact Us",
                  [Plan.Custom]: "Contact Us",
                }[currentPlan]) ||
              "Subscribe to Starter"
            }
            billingPeriod={billingPeriod}
            disabled={currentPlan === Plan.Starter || !user?.is_primary}
          />
        </div>
        <div className="col-3">
          <PlanRegular
            plan={Plan.Essential}
            currentPlan={currentPlan as Plan}
            name="Essential"
            subtitle="Industry standard monitoring essentials"
            handleCheckout={handleCheckout}
            startHereText="Everything in Starter, and:"
            btnText={
              (currentPlan &&
                {
                  [Plan.Starter]: "Subscribe to Essential",
                  [Plan.Essential]: "Current Plan",
                  [Plan.Premium]: "Contact Us",
                  [Plan.Custom]: "Contact Us",
                }[currentPlan]) ||
              "Subscribe to Essential"
            }
            billingPeriod={billingPeriod}
            disabled={currentPlan === Plan.Essential || !user?.is_primary}
          />
        </div>

        <div className="col-3">
          <PlanRegular
            plan={Plan.Premium}
            currentPlan={currentPlan as Plan}
            name="Premium"
            subtitle="The best plan for growing companies"
            handleCheckout={handleCheckout}
            featured={true}
            startHereText="Everything in Essential, and:"
            btnText={
              (currentPlan &&
                {
                  [Plan.Starter]: "Subscribe to Premium",
                  [Plan.Essential]: "Subscribe to Premium",
                  [Plan.Premium]: "Current Plan",
                  [Plan.Custom]: "Contact Us",
                }[currentPlan]) ||
              "Subscribe to Premium"
            }
            billingPeriod={billingPeriod}
            disabled={currentPlan === Plan.Premium || !user?.is_primary}
          />
        </div>
        <div className="col-3 d-flex align-items-stretch">
          <PlanCustom
            handleCheckout={(plan) => {
              handleCheckout(plan, []);
            }}
          />
        </div>
      </div>
    </>
  );
};
